import { supabaseClient } from './supabaseClient';

// Single source of truth for a member's two-sided record — served by the
// get_member_record SECURITY DEFINER RPC (Stage 36 / H6). The RPC enforces
// own-record-or-admin access, and resolves model-aware counterparties, references,
// the organizer name, and the latest attestation SERVER-SIDE. Both the admin
// ManageMembers modal and the member-facing FHA export consume this, so what is
// attested, shown, and exported can never drift. It is also the ONLY path a member
// has to their received side (they have no RLS access to peer payments — Stage 32).
//
// Returns (shape of the RPC's jsonb):
//   {
//     model,                                   // 'through_admin' | 'direct_to_collector'
//     member:    { id, name },
//     organizer: { name, complete },           // complete === false blocks the certified PDF
//     contributed: [{ round_number, cycle_number, date, amount, status, reference, paid_to }],
//     received:    [{ round_number, cycle_number, date, amount, status, reference, from }],
//     attestation: { attestation_id, statement, statement_version, statement_sha256,
//                    record_sha256, attested_at, attested_by_name, affirmed } | null
//   }
const EMPTY = {
  model: null,
  member: null,
  organizer: { name: null, complete: false },
  contributed: [],
  received: [],
  attestation: null,
};

export async function fetchMemberRecord({ memberId, groupId }) {
  const { data, error } = await supabaseClient.rpc('get_member_record', {
    p_group_id: groupId,
    p_member_db_id: memberId,
  });
  if (error) throw error;
  return data || EMPTY;
}

// Group record rows by cycle for an unambiguous multi-cycle display. Distinct
// cycles can repeat round numbers ("Round 2" in cycle 1 AND cycle 2), which looks
// like duplicates in a flat list — grouping + per-cycle subtotals tells the
// accumulated-savings-across-cycles story. p.cycle_number is the round's cycle
// (the engine copies r.cycle_number onto each payment). Returns groups sorted by
// cycle, rows within a cycle sorted by round.
export function groupByCycle(rows) {
  const map = new Map();
  for (const r of (rows || [])) {
    const c = r.cycle_number ?? null;
    if (!map.has(c)) map.set(c, []);
    map.get(c).push(r);
  }
  const groups = [...map.entries()].map(([cycle, rs]) => ({
    cycle,
    rows: rs.slice().sort((a, b) => (a.round_number ?? 0) - (b.round_number ?? 0)),
    subtotal: rs.reduce((s, r) => s + Number(r.amount || 0), 0),
  }));
  groups.sort((a, b) => (a.cycle ?? Infinity) - (b.cycle ?? Infinity));
  return groups;
}
